import { safeNextPath } from './sso.js'
import { hasCapability } from './permissions.mjs'

/** 页面状态以本站核验结果为准；上游故障与已经退出分别处理。 */
export function authRouteTarget(to, store) {
  if (store.errorStatus && store.errorStatus !== 401) {
    if (to.path !== '/login') {
      return { path: '/login', query: { auth_error: store.errorStatus === 403 ? 'denied' : 'unavailable', next: to.fullPath } }
    }
    return
  }
  if (to.path === '/login' && store.isAuthenticated) return safeNextPath(to.query?.next)
  if (to.meta?.requiresAuth && !store.hasRouteAccess) return { path: '/login', query: { next: to.fullPath } }
  if (to.meta?.requiredCapability && !hasCapability(store.userInfo, to.meta.requiredCapability)) return '/'
}

export async function checkAuthRoute(to, store) {
  try {
    if (to.path === '/login') await store.fetchUserInfo()
    else await store.initializeAuth()
  } catch {
    // 核验错误由 Store 保存，统一映射为禁止访问或可重试状态。
  }
  return authRouteTarget(to, store)
}

/** 恢复标签页或历史缓存时重新核验；不销毁仍然有效的业务页面。 */
export function installAuthLifecycle(router, store, win = window, doc = document) {
  let pending = null
  let stopped = false
  let needsReload = ''
  const recheck = () => {
    if (stopped || doc.visibilityState === 'hidden' || pending || store.loggingOut) return pending
    pending = (async () => {
      await router.isReady()
      if (stopped) return
      const previousId = store.userInfo?.id
      const version = store.authVersion
      const identityVersion = store.identityVersion
      try { await store.fetchUserInfo() } catch { /* 页面根据 errorStatus 展示核验结果。 */ }
      if (stopped || store.loggingOut || version !== store.authVersion) return
      if (previousId && store.isAuthenticated && previousId !== store.userInfo.id) needsReload = 'identity'
      else if (previousId && store.isAuthenticated && identityVersion !== store.identityVersion && !needsReload) needsReload = 'permissions'
      // 在途核验仍更新权限，但隐藏窗口不执行导航；恢复后保留清除旧身份页面的要求。
      if (doc.visibilityState === 'hidden') return
      const to = router.currentRoute.value
      // 暂时无法复核不驱离当前页面；下一次新路由准入仍由路由守卫校验。
      if (store.userInfo && store.errorStatus && ![401, 403].includes(store.errorStatus) && to.path !== '/login') return
      if (to.meta?.requiresAuth && store.isAuthenticated && needsReload) {
        const reason = needsReload
        needsReload = ''
        if (reason === 'identity') win.location.replace(to.fullPath)
        else win.location.reload()
        return
      }
      const target = authRouteTarget(to, store)
      if (target) await router.replace(target)
    })().finally(() => { pending = null })
    return pending
  }
  const visibility = () => {
    if (doc.visibilityState === 'visible') recheck()?.catch(() => {})
  }
  const pageshow = event => {
    if (event.persisted) recheck()?.catch(() => {})
  }
  doc.addEventListener('visibilitychange', visibility)
  win.addEventListener('pageshow', pageshow)
  win.addEventListener('microquake:recheck-identity', visibility)
  return () => {
    stopped = true
    doc.removeEventListener('visibilitychange', visibility)
    win.removeEventListener('pageshow', pageshow)
    win.removeEventListener('microquake:recheck-identity', visibility)
  }
}
